import React, { useEffect, useMemo, useState } from "react";
import { Crown, Save, Trophy } from "lucide-react";

const totalOf = (notes) => {
  const v = notes.filter((n) => n != null && n !== "").map(Number).filter((n) => !isNaN(n));
  if (!v.length) return null;
  const s = [...v].sort((a, b) => a - b);
  return (v.length >= 5 ? s.slice(1, -1) : s).reduce((a, b) => a + b, 0);
};

export const OverallVoting = ({ overallId, participants, numArbitros, votes, winnerId, onSaveVotes, onSetWinner }) => {
  const judges = useMemo(() => Array.from({ length: numArbitros }, (_, i) => String(i + 1)), [numArbitros]);
  const [grid, setGrid] = useState(votes || {});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!dirty) setGrid(votes || {}); }, [votes]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const list = participants.filter((p) => p.athlete_id).map((p) => ({ ...p, total: totalOf(judges.map((j) => grid[j]?.[p.athlete_id])) }));
    const ranked = list.filter((r) => r.total != null).sort((a, b) => a.total - b.total || (a.numero || 0) - (b.numero || 0));
    ranked.forEach((r, i) => { r.pos = i > 0 && ranked[i - 1].total === r.total ? ranked[i - 1].pos : i + 1; });
    return list;
  }, [participants, grid, judges]);
  const suggested = rows.find((r) => r.pos === 1);

  const setNote = (j, aid, val) => {
    setGrid((g) => ({ ...g, [j]: { ...(g[j] || {}), [aid]: val === "" ? null : Number(val) } }));
    setDirty(true);
  };
  const save = async () => { setSaving(true); try { await onSaveVotes(grid); setDirty(false); } finally { setSaving(false); } };

  if (!rows.length) return null;
  return (
    <div className="mt-5 border-t border-[#C9A84C]/20 pt-4" data-testid={`overall-voting-${overallId}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="overline flex items-center gap-2"><Trophy size={13} /> Votação do Overall</div>
        <div className="text-[11px] text-[var(--text-secondary)]">Lance a colocação dada por cada árbitro (1 = melhor). Menor soma vence{numArbitros >= 5 ? " · descarta maior e menor" : ""}.</div>
      </div>
      <div className="overflow-x-auto">
        <table className="table-dense">
          <thead>
            <tr>
              <th>Nº</th><th>Atleta</th>
              {judges.map((j) => <th key={j} className="text-center">Árb. {j}</th>)}
              <th className="text-right">Total</th><th className="text-center">Pos.</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isWinner = r.athlete_id === winnerId;
              return (
                <tr key={r.athlete_id} className={isWinner ? "bg-[#C9A84C]/10" : ""}>
                  <td className="tabular-nums font-bold">{r.numero ?? "—"}</td>
                  <td className="whitespace-nowrap">{isWinner && <Crown size={13} className="inline mr-1 text-[#C9A84C]" />}{r.nome}</td>
                  {judges.map((j) => (
                    <td key={j} className="text-center">
                      <input
                        type="number" min="1" max="99" step="1"
                        className="input-field !w-14 !py-1 !px-1 text-center tabular-nums"
                        value={grid[j]?.[r.athlete_id] ?? ""}
                        onChange={(e) => setNote(j, r.athlete_id, e.target.value)}
                        data-testid={`overall-vote-${overallId}-${r.athlete_id}-${j}`}
                      />
                    </td>
                  ))}
                  <td className="text-right tabular-nums font-bold" data-testid={`overall-total-${overallId}-${r.athlete_id}`}>{r.total ?? "—"}</td>
                  <td className="text-center tabular-nums">{r.pos ? `${r.pos}º` : "—"}</td>
                  <td className="text-right">
                    <button type="button" className={`btn-ghost !py-0.5 !px-2 !text-[11px] ${isWinner ? "!border-[#C9A84C] !text-[#C9A84C]" : ""}`}
                      onClick={() => onSetWinner(isWinner ? null : r.athlete_id)} data-testid={`overall-set-winner-${overallId}-${r.athlete_id}`}>
                      {isWinner ? "Campeão ✓" : "Definir campeão"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-3 mt-3">
        <div className="text-xs text-[var(--text-secondary)]" data-testid={`overall-suggestion-${overallId}`}>
          {suggested ? <>Pelas notas, o campeão geral seria <span className="text-[#C9A84C] font-bold">Nº {suggested.numero} {suggested.nome}</span>.</> : "Lance as notas para ver a sugestão de campeão."}
        </div>
        <div className="flex gap-2">
          {suggested && suggested.athlete_id !== winnerId && (
            <button type="button" className="btn-ghost text-xs inline-flex items-center gap-1" onClick={() => onSetWinner(suggested.athlete_id)} data-testid={`overall-confirm-suggested-${overallId}`}>
              <Crown size={13} /> Confirmar sugerido
            </button>
          )}
          <button type="button" className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" disabled={!dirty || saving} onClick={save} data-testid={`overall-save-votes-${overallId}`}>
            <Save size={13} /> {saving ? "Salvando..." : "Salvar notas"}
          </button>
        </div>
      </div>
    </div>
  );
};
